import { Injectable, inject, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

export interface LanguageConfig {
  code: string;         // e.g. "en-US", "hi-IN", "es-ES"
  language: string;     // e.g. "en", "hi", "es"
  region: string;       // e.g. "US", "IN", "ES"
  name: string;         // Native name, e.g. "English", "हिन्दी", "Español"
  english_name: string; // English name, e.g. "English", "Hindi", "Spanish"
  flag?: string;        // Emoji flag, e.g. "🇺🇸", "🇮🇳", "🇪🇸"
}

export const DEFAULT_LANGUAGE: LanguageConfig = {
  code: 'en-US',
  language: 'en',
  region: 'US',
  name: 'English',
  english_name: 'English',
  flag: '🇺🇸'
};

export const POPULAR_LANGUAGES: LanguageConfig[] = [
  { code: 'en-US', language: 'en', region: 'US', name: 'English', english_name: 'English', flag: '🇺🇸' },
  { code: 'hi-IN', language: 'hi', region: 'IN', name: 'हिन्दी', english_name: 'Hindi', flag: '🇮🇳' },
  { code: 'es-ES', language: 'es', region: 'ES', name: 'Español', english_name: 'Spanish', flag: '🇪🇸' },
  { code: 'fr-FR', language: 'fr', region: 'FR', name: 'Français', english_name: 'French', flag: '🇫🇷' },
  { code: 'de-DE', language: 'de', region: 'DE', name: 'Deutsch', english_name: 'German', flag: '🇩🇪' },
  { code: 'it-IT', language: 'it', region: 'IT', name: 'Italiano', english_name: 'Italian', flag: '🇮🇹' },
  { code: 'ja-JP', language: 'ja', region: 'JP', name: '日本語', english_name: 'Japanese', flag: '🇯🇵' },
  { code: 'ko-KR', language: 'ko', region: 'KR', name: '한국어', english_name: 'Korean', flag: '🇰🇷' },
  { code: 'pt-BR', language: 'pt', region: 'BR', name: 'Português (BR)', english_name: 'Portuguese (Brazil)', flag: '🇧🇷' },
  { code: 'ru-RU', language: 'ru', region: 'RU', name: 'Русский', english_name: 'Russian', flag: '🇷🇺' },
  { code: 'zh-CN', language: 'zh', region: 'CN', name: '简体中文', english_name: 'Chinese Simplified', flag: '🇨🇳' },
  { code: 'ar-SA', language: 'ar', region: 'SA', name: 'العربية', english_name: 'Arabic', flag: '🇸🇦' },
  { code: 'ta-IN', language: 'ta', region: 'IN', name: 'தமிழ்', english_name: 'Tamil', flag: '🇮🇳' },
  { code: 'te-IN', language: 'te', region: 'IN', name: 'తెలుగు', english_name: 'Telugu', flag: '🇮🇳' },
  { code: 'bn-BD', language: 'bn', region: 'BD', name: 'বাংলা', english_name: 'Bengali', flag: '🇧🇩' }
];

const STORAGE_KEY = 'app_language';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly http = inject(HttpClient);
  private readonly backendUrl = environment.backendUrl;

  private supportedLanguagesCache$: Observable<LanguageConfig[]> | null = null;
  private currentSupportedLanguages: LanguageConfig[] = [...POPULAR_LANGUAGES];

  private readonly currentLanguageSubject = new BehaviorSubject<LanguageConfig>(DEFAULT_LANGUAGE);
  public readonly currentLanguage$: Observable<LanguageConfig> = this.currentLanguageSubject.asObservable();
  public readonly currentLanguageSignal = signal<LanguageConfig>(DEFAULT_LANGUAGE);

  constructor() {
    this.initializeLanguage();
  }

  /**
   * Initializes the language based on:
   * 1. LocalStorage saved preference
   * 2. Browser language detection (navigator.language)
   * 3. Fallback to English (en-US)
   */
  private initializeLanguage(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const savedCode = localStorage.getItem(STORAGE_KEY);
    if (savedCode) {
      const match = this.findLanguageByCode(savedCode);
      if (match) {
        this.emitLanguage(match);
        return;
      }
    }

    // Auto-detect from browser language
    const detected = this.detectBrowserLanguage();
    this.emitLanguage(detected);
  }

  /**
   * Detects browser language and maps it to a supported TMDB translation.
   */
  public detectBrowserLanguage(): LanguageConfig {
    if (!isPlatformBrowser(this.platformId) || typeof navigator === 'undefined') {
      return DEFAULT_LANGUAGE;
    }

    const browserLanguages: string[] = [];
    if (Array.isArray(navigator.languages) && navigator.languages.length > 0) {
      browserLanguages.push(...navigator.languages);
    }
    if (navigator.language) {
      browserLanguages.push(navigator.language);
    }

    for (const rawLang of browserLanguages) {
      if (!rawLang) continue;
      const normalized = rawLang.replace('_', '-');

      // 1. Exact match (e.g. "hi-IN", "es-ES", "fr-FR")
      const exact = this.currentSupportedLanguages.find(
        l => l.code.toLowerCase() === normalized.toLowerCase()
      );
      if (exact) return exact;

      // 2. Language prefix match (e.g. "hi" -> "hi-IN", "fr" -> "fr-FR")
      const primaryLang = normalized.split('-')[0].toLowerCase();
      const prefixMatch = this.currentSupportedLanguages.find(
        l => l.language.toLowerCase() === primaryLang
      );
      if (prefixMatch) return prefixMatch;
    }

    return DEFAULT_LANGUAGE;
  }

  /**
   * Returns current active language config synchronously.
   */
  public get currentLanguage(): LanguageConfig {
    return this.currentLanguageSubject.value;
  }

  /**
   * Change current active language across the entire application.
   */
  public setLanguage(langOrCode: string | LanguageConfig): void {
    let targetConfig: LanguageConfig | undefined;

    if (typeof langOrCode === 'string') {
      targetConfig = this.findLanguageByCode(langOrCode);
      if (!targetConfig) {
        // Build fallback config if not in current list
        const [lang, reg] = langOrCode.split('-');
        targetConfig = {
          code: langOrCode,
          language: lang || 'en',
          region: reg || 'US',
          name: langOrCode,
          english_name: langOrCode,
          flag: '🌐'
        };
      }
    } else {
      targetConfig = langOrCode;
    }

    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem(STORAGE_KEY, targetConfig.code);
      } catch (e) {
        console.warn('Could not save language to localStorage', e);
      }
    }

    this.emitLanguage(targetConfig);
  }

  private emitLanguage(config: LanguageConfig): void {
    this.currentLanguageSubject.next(config);
    this.currentLanguageSignal.set(config);
  }

  private findLanguageByCode(code: string): LanguageConfig | undefined {
    const normalized = code.toLowerCase().replace('_', '-');
    return this.currentSupportedLanguages.find(l => l.code.toLowerCase() === normalized);
  }

  /**
   * Fetches supported languages from backend, combining primary translations with flags and names.
   */
  public getSupportedLanguages(): Observable<LanguageConfig[]> {
    if (this.supportedLanguagesCache$) {
      return this.supportedLanguagesCache$;
    }

    this.supportedLanguagesCache$ = this.http.get<LanguageConfig[]>(`${this.backendUrl}/api/conf/supported-languages`).pipe(
      map(data => (Array.isArray(data) && data.length > 0 ? data : POPULAR_LANGUAGES)),
      tap(languages => {
        this.currentSupportedLanguages = languages;
        // Re-check current active language in case richer native name became available
        const current = this.currentLanguageSubject.value;
        const enriched = languages.find(l => l.code.toLowerCase() === current.code.toLowerCase());
        if (enriched && (enriched.name !== current.name || enriched.flag !== current.flag)) {
          this.emitLanguage(enriched);
        }
      }),
      catchError(err => {
        console.warn('Failed to load supported languages from backend, using defaults', err);
        return of(POPULAR_LANGUAGES);
      }),
      shareReplay(1)
    );

    return this.supportedLanguagesCache$;
  }

  /**
   * TMDB Configuration API: Countries
   */
  public getCountries(): Observable<any[]> {
    return this.http.get<any[]>(`${this.backendUrl}/api/conf/countries`).pipe(
      catchError(() => of([]))
    );
  }

  /**
   * TMDB Configuration API: Languages
   */
  public getLanguages(): Observable<any[]> {
    return this.http.get<any[]>(`${this.backendUrl}/api/conf/languages`).pipe(
      catchError(() => of([]))
    );
  }

  /**
   * TMDB Configuration API: Timezones
   */
  public getTimezones(): Observable<any[]> {
    return this.http.get<any[]>(`${this.backendUrl}/api/conf/timezones`).pipe(
      catchError(() => of([]))
    );
  }

  /**
   * Returns localized UI text for common sections, tabs, and buttons.
   */
  public t(key: string): string {
    const lang = this.currentLanguageSubject.value?.language || 'en';
    return UI_TRANSLATIONS[lang]?.[key] || UI_TRANSLATIONS['en']?.[key] || key;
  }
}

export const UI_TRANSLATIONS: Record<string, Record<string, string>> = {
  'en': {
    'home': 'Home',
    'trending': 'Trending',
    'today': 'Today',
    'thisWeek': 'This Week',
    'topRated': 'Top Rated',
    'trailers': 'Trailers',
    'popular': 'Popular',
    'upcoming': 'Upcoming',
    'nowPlaying': 'Now Playing',
    'airingToday': 'Airing Today',
    'onTheAir': 'On TV',
    'streaming': 'Streaming',
    'inTheatre': 'In Theatres',
    'movies': 'Movies',
    'movie': 'Movie',
    'tv': 'TV Shows',
    'person': 'People',
    'popularMovies': 'Popular Movies',
    'popularTv': 'Popular TV Shows',
    'popularPeople': 'Popular People',
    'animation': 'Animation',
    'documentaries': 'Documentaries',
    'sciFi': 'Sci-Fi & Adventure',
    'trivia': 'Trivia',
    'blogs': 'Blogs',
    'watchTrailer': 'Watch Trailer',
    'playTrailer': 'Play Trailer',
    'getStarted': 'Get Started',
    'searchPlaceholder': 'Search movies, TV shows, actors...',
    'search': 'Search',
    'clearSearch': 'Clear search',
    'tryAgain': 'Try Again',
    'myProfile': 'My Profile',
    'editProfile': 'Edit Profile',
    'logout': 'Logout',
    'overview': 'Overview',
    'cast': 'Top Billed Cast',
    'castAndCrew': 'Cast & Crew',
    'recommendations': 'Recommendations',
    'similar': 'Similar Titles',
    'reviews': 'Reviews',
    'facts': 'Facts',
    'endOfResults': "You've reached the end of the collection.",
    'welcomeTitle': 'Welcome to The Scenetale',
    'welcomeSubtitle': 'Lights. Camera. Emotion. Dive into the world of cinematic wonder.',
    'discover': 'Discover',
    'movieOfTheDay': 'Movie of the Day',
    'actorOfTheDay': 'Actor of the Day',
    'directedBy': 'Directed by',
    'director': 'Director',
    'producer': 'Producer',
    'genres': 'Genres',
    'theatricalTrailer': 'Theatrical Trailer',
    'mainCast': 'Main Cast & Characters',
    'notableWorks': 'Notable Works & Filmography',
    'signIn': 'Sign In',
    'signUp': 'Sign Up',
    'login': 'Login',
    'loggingIn': 'Signing in...',
    'creatingAccount': 'Creating account...',
    'email': 'Email',
    'emailPlaceholder': 'Enter your email',
    'password': 'Password',
    'passwordPlaceholder': 'Enter your password',
    'passwordSignupPlaceholder': 'Password (min 8 characters)',
    'username': 'Username',
    'usernamePlaceholder': 'Choose a username',
    'firstName': 'First Name',
    'firstNamePlaceholder': 'First name (optional)',
    'lastName': 'Last Name',
    'lastNamePlaceholder': 'Last name (optional)',
    'dontHaveAccount': "Don't have an account?",
    'alreadyHaveAccount': 'Already have an account?',
    'backToHome': 'Back to Home',
    'returnToSite': 'Return to Site',
    'orContinueWith': 'Or continue with',
    'welcomeToScenetale': 'Welcome to Scenetale',
    'authSubtitle': 'Sign in to save favorites, track your watchlist, and keep your cinema world in sync.',
    'memberLogin': 'Member Login',
    'memberSignUp': 'Join Scenetale',
    'pickUpWhereLeftOff': 'Pick up where you left off',
    'createYourAccount': 'Create your account',
    'authCopyLogin': 'Use your preferred credentials or sign-in provider to continue exploring films, quizzes, and editor picks.',
    'authCopySignup': 'Create your account to track movies, write reviews, and save favorites across devices.',
    'adminLoginTitle': 'Scenetale Admin',
    'adminLoginSubtitle': 'Editorial tools, quiz management, and backstage controls live here. Sign in only with an approved admin account.',
    'adminConsole': 'Admin Console',
    'adminConsoleTitle': 'Sign in to manage the platform',
    'adminConsoleCopy': 'Use your authorized credentials to enter the control room for blogs, quizzes, and moderation tools.',
    'adminNote': 'Authorized team access only. Admin actions are logged and monitored.',
    'restrictedAccess': 'Restricted Access',
    'movieLovers': 'Movie Lovers',
    'showPassword': 'Show password',
    'hidePassword': 'Hide password',
    'language': 'Language',
    'loginRequiredTitle': 'Login Required',
    'loginRequiredMessage': 'Please log in to perform this activity.',
    'validationError': 'Required Fields',
    'enterBothEmailPassword': 'Enter both email and password.',
    'emailUserPassRequired': 'Email, username, and password are required.',
    'passwordMinLength': 'Password must be at least 8 characters.'
  },
  'hi': {
    'home': 'होम',
    'trending': 'ट्रेंडिंग',
    'today': 'आज',
    'thisWeek': 'इस सप्ताह',
    'topRated': 'टॉप रेटेड',
    'trailers': 'ट्रेलर्स',
    'popular': 'लोकप्रिय',
    'upcoming': 'आगामी',
    'nowPlaying': 'सिनेमाघरों में',
    'airingToday': 'आज प्रसारित',
    'onTheAir': 'टीवी पर प्रसारित',
    'streaming': 'स्ट्रीमिंग',
    'inTheatre': 'सिनेमाघरों में',
    'movies': 'फ़िल्में',
    'movie': 'फ़िल्म',
    'tv': 'टीवी शो',
    'person': 'कलाकार',
    'popularMovies': 'लोकप्रिय फ़िल्में',
    'popularTv': 'लोकप्रिय टीवी शो',
    'popularPeople': 'लोकप्रिय कलाकार',
    'animation': 'एनीमेशन',
    'documentaries': 'डॉक्यूमेंट्री',
    'sciFi': 'रोमांच और विज्ञान कथा',
    'trivia': 'प्रश्नोत्तरी',
    'blogs': 'ब्लॉग',
    'watchTrailer': 'ट्रेलर देखें',
    'playTrailer': 'ट्रेलर चलाएं',
    'getStarted': 'शुरू करें',
    'searchPlaceholder': 'फ़िल्में, टीवी शो, कलाकार खोजें...',
    'search': 'खोजें',
    'clearSearch': 'खोज साफ़ करें',
    'tryAgain': 'पुनः प्रयास करें',
    'myProfile': 'मेरी प्रोफ़ाइल',
    'editProfile': 'प्रोफ़ाइल संपादित करें',
    'logout': 'लॉग आउट',
    'overview': 'सारांश',
    'cast': 'प्रमुख कलाकार',
    'castAndCrew': 'कलाकार और क्रू',
    'recommendations': 'सुझाव',
    'similar': 'समान शीर्षक',
    'reviews': 'समीक्षाएँ',
    'facts': 'तथ्य',
    'endOfResults': 'आप संग्रह के अंत तक पहुँच चुके हैं।',
    'welcomeTitle': 'द सीनटेल में आपका स्वागत है',
    'welcomeSubtitle': 'प्रकाश। कैमरा। भावना। सिनेमाई आश्चर्य की दुनिया में उतरें।',
    'discover': 'खोजें',
    'movieOfTheDay': 'आज की फ़िल्म',
    'actorOfTheDay': 'आज के अभिनेता',
    'directedBy': 'निर्देशक',
    'director': 'निर्देशक',
    'producer': 'निर्माता',
    'genres': 'शैलियाँ',
    'theatricalTrailer': 'थिएट्रिकल ट्रेलर',
    'mainCast': 'मुख्य कलाकार और पात्र',
    'notableWorks': 'उल्लेखनीय कार्य और फ़िल्मोग्राफी',
    'signIn': 'साइन इन करें',
    'signUp': 'साइन अप करें',
    'login': 'लॉग इन',
    'loggingIn': 'लॉग इन हो रहा है...',
    'creatingAccount': 'खाता बनाया जा रहा है...',
    'email': 'ईमेल',
    'emailPlaceholder': 'अपना ईमेल दर्ज करें',
    'password': 'पासवर्ड',
    'passwordPlaceholder': 'अपना पासवर्ड दर्ज करें',
    'passwordSignupPlaceholder': 'पासवर्ड (कम से कम 8 वर्ण)',
    'username': 'यूज़रनेम',
    'usernamePlaceholder': 'यूज़रनेम चुनें',
    'firstName': 'पहला नाम',
    'firstNamePlaceholder': 'पहला नाम (वैकल्पिक)',
    'lastName': 'अंतिम नाम',
    'lastNamePlaceholder': 'अंतिम नाम (वैकल्पिक)',
    'dontHaveAccount': 'क्या आपका खाता नहीं है?',
    'alreadyHaveAccount': 'क्या पहले से खाता है?',
    'backToHome': 'होम पर वापस जाएं',
    'returnToSite': 'साइट पर वापस जाएं',
    'orContinueWith': 'या इनके माध्यम से जारी रखें',
    'welcomeToScenetale': 'सीनटेल में आपका स्वागत है',
    'authSubtitle': 'पसंदीदा सेव करने, वॉचलिस्ट ट्रैक करने और सिनेमा की दुनिया से जुड़े रहने के लिए साइन इन करें।',
    'memberLogin': 'सदस्य लॉगिन',
    'memberSignUp': 'सीनटेल से जुड़ें',
    'pickUpWhereLeftOff': 'वहीं से शुरू करें जहाँ आपने छोड़ा था',
    'createYourAccount': 'अपना खाता बनाएं',
    'authCopyLogin': 'फ़िल्में, क्विज़ और संपादकीय चयन देखने के लिए अपनी पसंदीदा विधि से साइन इन करें।',
    'authCopySignup': 'फ़िल्में ट्रैक करने, समीक्षाएं लिखने और पसंदीदा सहेजने के लिए अपना खाता बनाएं।',
    'adminLoginTitle': 'सीनटेल एडमिन',
    'adminLoginSubtitle': 'संपादकीय उपकरण और नियंत्रण यहाँ हैं। केवल स्वीकृत व्यवस्थापक खाते से साइन इन करें।',
    'adminConsole': 'व्यवस्थापक कंसोल',
    'adminConsoleTitle': 'प्लेटफ़ॉर्म प्रबंधन के लिए साइन इन करें',
    'adminConsoleCopy': 'कंट्रोल रूम में प्रवेश करने के लिए अपने अधिकृत क्रेडेंशियल्स का उपयोग करें।',
    'adminNote': 'केवल अधिकृत टीम पहुंच। सभी कार्यों की निगरानी की जाती है।',
    'restrictedAccess': 'प्रतिबंधित पहुंच',
    'movieLovers': 'सिनेमा प्रेमी',
    'showPassword': 'पासवर्ड दिखाएं',
    'hidePassword': 'पासवर्ड छुपाएं',
    'language': 'भाषा',
    'loginRequiredTitle': 'लॉगिन आवश्यक है',
    'loginRequiredMessage': 'यह गतिविधि करने के लिए कृपया लॉगिन करें।',
    'validationError': 'आवश्यक फ़ील्ड',
    'enterBothEmailPassword': 'कृपया ईमेल और पासवर्ड दोनों दर्ज करें।',
    'emailUserPassRequired': 'ईमेल, यूज़रनेम और पासवर्ड आवश्यक हैं।',
    'passwordMinLength': 'पासवर्ड कम से कम 8 वर्णों का होना चाहिए।'
  },
  'es': {
    'home': 'Inicio',
    'trending': 'Tendencias',
    'today': 'Hoy',
    'thisWeek': 'Esta semana',
    'topRated': 'Mejor valoradas',
    'trailers': 'Tráilers',
    'popular': 'Populares',
    'upcoming': 'Próximamente',
    'nowPlaying': 'En cines',
    'airingToday': 'Emitiéndose hoy',
    'onTheAir': 'En emisión',
    'streaming': 'En streaming',
    'inTheatre': 'En cines',
    'movies': 'Películas',
    'movie': 'Película',
    'tv': 'Series de TV',
    'person': 'Personas',
    'popularMovies': 'Películas populares',
    'popularTv': 'Series populares',
    'popularPeople': 'Personas populares',
    'animation': 'Animación',
    'documentaries': 'Documentales',
    'sciFi': 'Ciencia ficción y aventuras',
    'trivia': 'Trivia',
    'blogs': 'Blogs',
    'watchTrailer': 'Ver tráiler',
    'playTrailer': 'Reproducir tráiler',
    'getStarted': 'Empezar',
    'searchPlaceholder': 'Buscar películas, series, actores...',
    'search': 'Buscar',
    'clearSearch': 'Limpiar búsqueda',
    'tryAgain': 'Intentar de nuevo',
    'myProfile': 'Mi Perfil',
    'editProfile': 'Editar Perfil',
    'logout': 'Cerrar sesión',
    'overview': 'Resumen',
    'cast': 'Reparto principal',
    'castAndCrew': 'Reparto y equipo',
    'recommendations': 'Recomendaciones',
    'similar': 'Títulos similares',
    'reviews': 'Reseñas',
    'facts': 'Datos',
    'endOfResults': 'Has llegado al final de la colección.',
    'welcomeTitle': 'Bienvenido a The Scenetale',
    'welcomeSubtitle': 'Luces. Cámara. Emoción. Sumérgete en el mundo del cine.',
    'discover': 'Descubrir',
    'movieOfTheDay': 'Película del día',
    'actorOfTheDay': 'Actor del día',
    'directedBy': 'Dirigida por',
    'director': 'Director',
    'producer': 'Productor',
    'genres': 'Géneros',
    'theatricalTrailer': 'Tráiler de cine',
    'mainCast': 'Reparto principal y personajes',
    'notableWorks': 'Obras destacadas y filmografía',
    'signIn': 'Iniciar Sesión',
    'signUp': 'Registrarse',
    'login': 'Iniciar Sesión',
    'loggingIn': 'Iniciando sesión...',
    'creatingAccount': 'Creando cuenta...',
    'email': 'Correo electrónico',
    'emailPlaceholder': 'Ingresa tu correo electrónico',
    'password': 'Contraseña',
    'passwordPlaceholder': 'Ingresa tu contraseña',
    'passwordSignupPlaceholder': 'Contraseña (mínimo 8 caracteres)',
    'username': 'Nombre de usuario',
    'usernamePlaceholder': 'Elige un nombre de usuario',
    'firstName': 'Nombre',
    'firstNamePlaceholder': 'Nombre (opcional)',
    'lastName': 'Apellido',
    'lastNamePlaceholder': 'Apellido (opcional)',
    'dontHaveAccount': '¿No tienes una cuenta?',
    'alreadyHaveAccount': '¿Ya tienes una cuenta?',
    'backToHome': 'Volver al Inicio',
    'returnToSite': 'Volver al sitio',
    'orContinueWith': 'O continuar con',
    'welcomeToScenetale': 'Bienvenido a Scenetale',
    'authSubtitle': 'Inicia sesión para guardar favoritos, seguir tu lista y mantener tu mundo de cine sincronizado.',
    'memberLogin': 'Acceso de Miembros',
    'memberSignUp': 'Únete a Scenetale',
    'pickUpWhereLeftOff': 'Retoma donde lo dejaste',
    'createYourAccount': 'Crea tu cuenta',
    'authCopyLogin': 'Usa tus credenciales o proveedor preferido para seguir explorando películas, trivias y selecciones del editor.',
    'authCopySignup': 'Crea tu cuenta para seguir películas, escribir reseñas y guardar tus favoritos.',
    'adminLoginTitle': 'Scenetale Admin',
    'adminLoginSubtitle': 'Herramientas editoriales y de control. Inicia sesión únicamente con una cuenta autorizada.',
    'adminConsole': 'Consola de Administración',
    'adminConsoleTitle': 'Inicia sesión para administrar',
    'adminConsoleCopy': 'Usa tus credenciales para acceder al centro de control.',
    'adminNote': 'Solo acceso autorizado. Las acciones son registradas.',
    'restrictedAccess': 'Acceso Restringido',
    'movieLovers': 'Amantes del Cine',
    'showPassword': 'Mostrar contraseña',
    'hidePassword': 'Ocultar contraseña',
    'language': 'Idioma',
    'loginRequiredTitle': 'Inicio de sesión requerido',
    'loginRequiredMessage': 'Por favor inicia sesión para realizar esta actividad.',
    'validationError': 'Campos requeridos',
    'enterBothEmailPassword': 'Ingresa tanto el correo como la contraseña.',
    'emailUserPassRequired': 'El correo, el nombre de usuario y la contraseña son obligatorios.',
    'passwordMinLength': 'La contraseña debe tener al menos 8 caracteres.'
  },
  'fr': {
    'home': 'Accueil',
    'trending': 'Tendances',
    'today': "Aujourd'hui",
    'thisWeek': 'Cette semaine',
    'topRated': 'Les mieux notés',
    'trailers': 'Bandes-annonces',
    'popular': 'Populaires',
    'upcoming': 'À venir',
    'nowPlaying': 'En salles',
    'airingToday': 'Diffusé aujourd’hui',
    'onTheAir': 'À la télévision',
    'streaming': 'En streaming',
    'inTheatre': 'En salles',
    'movies': 'Films',
    'movie': 'Film',
    'tv': 'Séries TV',
    'person': 'Personnes',
    'popularMovies': 'Films populaires',
    'popularTv': 'Séries populaires',
    'popularPeople': 'Personnes populaires',
    'animation': 'Animation',
    'documentaries': 'Documentaires',
    'sciFi': 'Science-fiction & Aventure',
    'trivia': 'Quiz',
    'blogs': 'Blogs',
    'watchTrailer': 'Regarder la bande-annonce',
    'playTrailer': 'Lire la bande-annonce',
    'getStarted': 'Commencer',
    'searchPlaceholder': 'Rechercher des films, séries, acteurs...',
    'search': 'Rechercher',
    'clearSearch': 'Effacer la recherche',
    'tryAgain': 'Réessayer',
    'myProfile': 'Mon Profil',
    'editProfile': 'Modifier le profil',
    'logout': 'Déconnexion',
    'overview': 'Synopsis',
    'cast': 'Acteurs principaux',
    'castAndCrew': 'Distribution et équipe',
    'recommendations': 'Recommandations',
    'similar': 'Titres similaires',
    'reviews': 'Critiques',
    'facts': 'Informations',
    'endOfResults': 'Vous avez atteint la fin de la collection.',
    'welcomeTitle': 'Bienvenue sur The Scenetale',
    'welcomeSubtitle': "Lumières. Caméra. Émotion. Plongez dans l'univers cinématographique.",
    'discover': 'Découvrir',
    'movieOfTheDay': 'Film du jour',
    'actorOfTheDay': 'Acteur du jour',
    'directedBy': 'Réalisé par',
    'director': 'Réalisateur',
    'producer': 'Producteur',
    'genres': 'Genres',
    'theatricalTrailer': 'Bande-annonce cinéma',
    'mainCast': 'Distribution principale et personnages',
    'notableWorks': 'Œuvres notables et filmographie',
    'signIn': 'Se connecter',
    'signUp': "S'inscrire",
    'login': 'Connexion',
    'loggingIn': 'Connexion en cours...',
    'creatingAccount': 'Création du compte...',
    'email': 'E-mail',
    'emailPlaceholder': 'Entrez votre e-mail',
    'password': 'Mot de passe',
    'passwordPlaceholder': 'Entrez votre mot de passe',
    'passwordSignupPlaceholder': 'Mot de passe (8 caractères min)',
    'username': "Nom d'utilisateur",
    'usernamePlaceholder': "Choisissez un nom d'utilisateur",
    'firstName': 'Prénom',
    'firstNamePlaceholder': 'Prénom (facultatif)',
    'lastName': 'Nom',
    'lastNamePlaceholder': 'Nom (facultatif)',
    'dontHaveAccount': "Vous n'avez pas de compte ?",
    'alreadyHaveAccount': 'Vous avez déjà un compte ?',
    'backToHome': "Retour à l'accueil",
    'returnToSite': 'Retourner sur le site',
    'orContinueWith': 'Ou continuer avec',
    'welcomeToScenetale': 'Bienvenue sur Scenetale',
    'authSubtitle': 'Connectez-vous pour enregistrer vos favoris, suivre votre watchlist et synchroniser votre univers cinéma.',
    'memberLogin': 'Connexion Membre',
    'memberSignUp': 'Rejoindre Scenetale',
    'pickUpWhereLeftOff': 'Reprenez là où vous en étiez',
    'createYourAccount': 'Créez votre compte',
    'authCopyLogin': 'Utilisez vos identifiants ou votre compte préféré pour explorer films, quiz et sélections.',
    'authCopySignup': 'Créez votre compte pour suivre des films, écrire des critiques et enregistrer vos favoris.',
    'adminLoginTitle': 'Scenetale Admin',
    'adminLoginSubtitle': 'Outils éditoriaux et gestion. Connectez-vous avec un compte administrateur approuvé.',
    'adminConsole': "Console d'administration",
    'adminConsoleTitle': 'Connectez-vous pour administrer',
    'adminConsoleCopy': 'Accédez au centre de contrôle des blogs, quiz et modération.',
    'adminNote': 'Accès réservé aux personnes autorisées.',
    'restrictedAccess': 'Accès Restreint',
    'movieLovers': 'Passionnés de Cinéma',
    'showPassword': 'Afficher le mot de passe',
    'hidePassword': 'Masquer le mot de passe',
    'language': 'Langue',
    'loginRequiredTitle': 'Connexion requise',
    'loginRequiredMessage': 'Veuillez vous connecter pour effectuer cette activité.',
    'validationError': 'Champs obligatoires',
    'enterBothEmailPassword': 'Entrez à la fois votre e-mail et votre mot de passe.',
    'emailUserPassRequired': "L'e-mail, le nom d'utilisateur et le mot de passe sont requis.",
    'passwordMinLength': 'Le mot de passe doit comporter au moins 8 caractères.'
  },
  'de': {
    'home': 'Startseite',
    'trending': 'Trends',
    'today': 'Heute',
    'thisWeek': 'Diese Woche',
    'topRated': 'Bestbewertet',
    'trailers': 'Trailer',
    'popular': 'Beliebt',
    'upcoming': 'Demnächst',
    'nowPlaying': 'Im Kino',
    'airingToday': 'Heute im TV',
    'onTheAir': 'Aktuell im Fernsehen',
    'streaming': 'Streaming',
    'inTheatre': 'Im Kino',
    'movies': 'Filme',
    'movie': 'Film',
    'tv': 'Serien',
    'person': 'Personen',
    'popularMovies': 'Beliebte Filme',
    'popularTv': 'Beliebte Serien',
    'popularPeople': 'Beliebte Stars',
    'animation': 'Animation',
    'documentaries': 'Dokumentationen',
    'sciFi': 'Sci-Fi & Abenteuer',
    'trivia': 'Quiz',
    'blogs': 'Blogs',
    'watchTrailer': 'Trailer ansehen',
    'playTrailer': 'Trailer abspielen',
    'getStarted': 'Loslegen',
    'searchPlaceholder': 'Filme, Serien, Schauspieler suchen...',
    'search': 'Suchen',
    'clearSearch': 'Suche löschen',
    'tryAgain': 'Erneut versuchen',
    'myProfile': 'Mein Profil',
    'editProfile': 'Profil bearbeiten',
    'logout': 'Abmelden',
    'overview': 'Übersicht',
    'cast': 'Hauptbesetzung',
    'castAndCrew': 'Besetzung & Stab',
    'recommendations': 'Empfehlungen',
    'similar': 'Ähnliche Titel',
    'reviews': 'Bewertungen',
    'facts': 'Fakten',
    'endOfResults': 'Du hast das Ende der Sammlung erreicht.',
    'welcomeTitle': 'Willkommen bei The Scenetale',
    'welcomeSubtitle': 'Licht. Kamera. Emotion. Tauchen Sie ein in die Welt des Kinos.',
    'discover': 'Entdecken',
    'movieOfTheDay': 'Film des Tages',
    'actorOfTheDay': 'Schauspieler des Tages',
    'directedBy': 'Regie von',
    'director': 'Regisseur',
    'producer': 'Produzent',
    'genres': 'Genres',
    'theatricalTrailer': 'Kinotrailer',
    'mainCast': 'Hauptbesetzung & Charaktere',
    'notableWorks': 'Bemerkenswerte Werke & Filmografie',
    'signIn': 'Anmelden',
    'signUp': 'Registrieren',
    'login': 'Anmelden',
    'loggingIn': 'Anmeldung läuft...',
    'creatingAccount': 'Konto wird erstellt...',
    'email': 'E-Mail',
    'emailPlaceholder': 'E-Mail eingeben',
    'password': 'Passwort',
    'passwordPlaceholder': 'Passwort eingeben',
    'passwordSignupPlaceholder': 'Passwort (mind. 8 Zeichen)',
    'username': 'Benutzername',
    'usernamePlaceholder': 'Benutzernamen wählen',
    'firstName': 'Vorname',
    'firstNamePlaceholder': 'Vorname (optional)',
    'lastName': 'Nachname',
    'lastNamePlaceholder': 'Nachname (optional)',
    'dontHaveAccount': 'Noch kein Konto?',
    'alreadyHaveAccount': 'Bereits ein Konto?',
    'backToHome': 'Zurück zur Startseite',
    'returnToSite': 'Zurück zur Website',
    'orContinueWith': 'Oder fortfahren mit',
    'welcomeToScenetale': 'Willkommen bei Scenetale',
    'authSubtitle': 'Melden Sie sich an, um Favoriten zu speichern, Ihre Watchlist zu verfolgen und synchron zu bleiben.',
    'memberLogin': 'Mitglieder-Login',
    'memberSignUp': 'Scenetale beitreten',
    'pickUpWhereLeftOff': 'Dort weitermachen, wo Sie aufgehört haben',
    'createYourAccount': 'Konto erstellen',
    'authCopyLogin': 'Verwenden Sie Ihre Anmeldedaten, um Filme, Quizze und Empfehlungen zu entdecken.',
    'authCopySignup': 'Erstellen Sie Ihr Konto, um Filme zu verfolgen, Rezensionen zu schreiben und Favoriten zu speichern.',
    'adminLoginTitle': 'Scenetale Admin',
    'adminLoginSubtitle': 'Verwaltungstools und Redaktion. Bitte nur mit einem autorisierten Administratorkonto anmelden.',
    'adminConsole': 'Admin-Konsole',
    'adminConsoleTitle': 'Anmelden zur Verwaltung',
    'adminConsoleCopy': 'Verwenden Sie Ihre Zugangsdaten für den Kontrollbereich.',
    'adminNote': 'Nur für autorisierte Teammitglieder.',
    'restrictedAccess': 'Eingeschränkter Zugriff',
    'movieLovers': 'Filmliebhaber',
    'showPassword': 'Passwort anzeigen',
    'hidePassword': 'Passwort verbergen',
    'language': 'Sprache',
    'loginRequiredTitle': 'Anmeldung erforderlich',
    'loginRequiredMessage': 'Bitte melden Sie sich an, um diese Aktivität auszuführen.',
    'validationError': 'Erforderliche Felder',
    'enterBothEmailPassword': 'Geben Sie sowohl E-Mail als auch Passwort ein.',
    'emailUserPassRequired': 'E-Mail, Benutzername und Passwort sind erforderlich.',
    'passwordMinLength': 'Das Passwort muss mindestens 8 Zeichen lang sein.'
  }
};
